// ADMIN — TIENDA DE PERSONALIZABLES
//
// Cuatro pestañas:
//   · Pedidos      — revisar solicitudes, cobrar y avanzar estados. Es lo primero
//                    que se ve porque es lo único que exige atención diaria.
//   · Productos    — catálogo (precio en €, stock, fotos).
//   · Contabilidad — la Google Sheet publicada, leída por Cloud Function.
//   · Ajustes      — datos de cobro (Bizum / IBAN), hoja y términos.
//
// El catálogo y los ajustes se escriben DIRECTO desde el cliente con regla
// `isAdmin()`. Todo lo que toca un pedido va por Cloud Function.
//
// Criterio visual del panel: lo urgente arriba y con color propio, lo demás
// plegado. Cada pedido lleva un CARRIL DE COLOR a la izquierda con el color de
// su estado, para poder barrer la lista de un vistazo sin leer las etiquetas.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { sanitizeFirestoreData } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import type { PackProduct, PackOrder, PackOrderStatus, PacksShopConfig, PacksPaymentInfo } from '@/lib/types';
import {
  createPackProduct, updatePackProduct, deletePackProduct, uploadPackImage,
  getPacksShopConfig, savePacksShopConfig, getPacksPaymentConfig, savePacksPaymentConfig,
  packsShop_reviewRequest, packsShop_adminSetStatus, packsShop_markOrderSeen,
} from '@/lib/actions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { RichText } from '@/components/ui/rich-text';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ESTADOS, AVANCE, EstadoBadge, EstadoTimeline, ImageGrid, OrderChat,
  formatEur, formatFechaHora, hexToRgba, stockInfo,
} from '@/components/tienda/ui';
import { Contabilidad } from '@/components/tienda/Contabilidad';
import {
  Loader2, Plus, Pencil, Trash2, Gem, ImagePlus, Package, Search, Check, X,
  Settings, ClipboardList, Inbox, MessageSquare, CreditCard, AlertTriangle,
  ChevronDown, ChevronUp, Ban, Euro, Boxes, Table2, Zap, User, Building2, Clock,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

type Tab = 'pedidos' | 'productos' | 'contabilidad' | 'ajustes';

export default function AdminPersonalizablesPage() {
  const { userData, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('pedidos');

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true });
  }, [userData, loading, navigate]);

  const productsQuery = useMemo(() => query(collection(db, 'packs_products'), orderBy('order', 'asc')), []);
  const { data: rawProducts } = useCollection<PackProduct>(productsQuery);
  const products = useMemo(
    () => (rawProducts || []).map((p) => sanitizeFirestoreData(p) as PackProduct),
    [rawProducts],
  );

  // Los pedidos sí se pueden ordenar en la query: el admin los lee todos, sin
  // filtro de igualdad que obligue a un índice compuesto.
  const ordersQuery = useMemo(() => query(collection(db, 'packs_orders'), orderBy('createdAt', 'desc')), []);
  const { data: rawOrders, isLoading: cargandoPedidos } = useCollection<PackOrder>(ordersQuery);
  const orders = useMemo(
    () => (rawOrders || []).map((o) => sanitizeFirestoreData(o) as PackOrder),
    [rawOrders],
  );

  const stats = useMemo(() => ({
    pendientes: orders.filter((o) => o.status === 'pendiente').length,
    porCobrar: orders.filter((o) => o.status === 'pago_reportado').length,
    activos: orders.filter((o) => ['aceptado', 'pago_reportado', 'en_proceso', 'terminado', 'en_mod'].includes(o.status)).length,
    ingresos: orders
      .filter((o) => ['en_proceso', 'terminado', 'en_mod', 'entregado'].includes(o.status))
      .reduce((n, o) => n + (Number(o.priceEur) || 0), 0),
  }), [orders]);

  if (loading || userData?.role !== 'admin') {
    return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-primary" /></div>;
  }

  const aviso = stats.pendientes + stats.porCobrar;

  return (
    <div className="space-y-6 pb-14">
      {/* ── Hero ── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-emerald-500/15 via-card/45 to-card/20 p-6 md:p-7"
      >
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center gap-5">
          <div className="grid h-14 w-14 place-items-center rounded-2xl border border-emerald-400/30 bg-emerald-500/15 text-emerald-300">
            <Gem className="h-7 w-7" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-headline text-3xl font-bold md:text-4xl">Tienda de Personalizables</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">Solicitudes, cobros, catálogo y contabilidad.</p>
          </div>
        </div>

        <div className="relative mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile icon={Inbox}         label="Por revisar"        value={String(stats.pendientes)} color="#f59e0b" alerta={stats.pendientes > 0} onClick={() => setTab('pedidos')} />
          <Tile icon={CreditCard}    label="Pagos por confirmar" value={String(stats.porCobrar)} color="#a78bfa" alerta={stats.porCobrar > 0} onClick={() => setTab('pedidos')} />
          <Tile icon={ClipboardList} label="Pedidos activos"    value={String(stats.activos)}   color="#38bdf8" />
          <Tile icon={Euro}          label="Cobrado"            value={formatEur(stats.ingresos)} color="#34d399" />
        </div>
      </motion.div>

      {/* ── Pestañas (pegadas arriba: la lista de pedidos es larga) ── */}
      <div className="sticky top-0 z-30 -mx-4 border-b border-white/5 bg-background/85 px-4 py-2.5 backdrop-blur-md md:-mx-6 md:px-6 lg:-mx-8 lg:px-8">
        <div className="flex flex-wrap gap-1.5">
          {([
            ['pedidos', 'Pedidos', ClipboardList],
            ['productos', 'Productos', Package],
            ['contabilidad', 'Contabilidad', Table2],
            ['ajustes', 'Ajustes', Settings],
          ] as const).map(([k, label, Icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`relative flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-all ${
                tab === k
                  ? 'border-primary/40 bg-primary/15 text-primary shadow-[0_0_16px_rgba(250,204,21,0.1)]'
                  : 'border-white/8 bg-card/40 text-muted-foreground hover:border-white/15 hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
              {k === 'pedidos' && aviso > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-br from-primary to-amber-500 px-1 text-[10px] font-bold text-primary-foreground">
                  {aviso}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {tab === 'pedidos' && <PanelPedidos orders={orders} cargando={cargandoPedidos} />}
      {tab === 'productos' && <PanelProductos products={products} />}
      {tab === 'contabilidad' && <Contabilidad orders={orders} />}
      {tab === 'ajustes' && <PanelAjustes />}
    </div>
  );
}

function Tile({ icon: Icon, label, value, color, alerta, onClick }: {
  icon: React.ElementType; label: string; value: string; color: string; alerta?: boolean; onClick?: () => void;
}) {
  const Comp: any = onClick ? 'button' : 'div';
  return (
    <Comp
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl border p-4 text-left transition-transform ${onClick ? 'hover:-translate-y-0.5' : ''}`}
      style={{ borderColor: hexToRgba(color, alerta ? 0.45 : 0.18), background: hexToRgba(color, alerta ? 0.14 : 0.06) }}
    >
      {alerta && (
        <span className="absolute right-3 top-3 h-2 w-2 animate-pulse rounded-full" style={{ background: color }} />
      )}
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color }}>{label}</span>
      </div>
      <p className="mt-1.5 font-headline text-2xl font-black">{value}</p>
    </Comp>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PEDIDOS
// ═══════════════════════════════════════════════════════════════════════════

const FILTROS: { key: string; label: string; test: (o: PackOrder) => boolean }[] = [
  { key: 'accion',    label: 'Requieren acción', test: (o) => o.status === 'pendiente' || o.status === 'pago_reportado' },
  { key: 'activos',   label: 'Activos',          test: (o) => ['aceptado', 'pago_reportado', 'en_proceso', 'terminado', 'en_mod'].includes(o.status) },
  { key: 'cerrados',  label: 'Cerrados',         test: (o) => ['entregado', 'cancelado', 'rechazado'].includes(o.status) },
  { key: 'todos',     label: 'Todos',            test: () => true },
];

function PanelPedidos({ orders, cargando }: { orders: PackOrder[]; cargando: boolean }) {
  const [filtro, setFiltro] = useState('accion');
  const [busca, setBusca] = useState('');

  const visibles = useMemo(() => {
    const f = FILTROS.find((x) => x.key === filtro) || FILTROS[3];
    const q = busca.trim().toLowerCase();
    return orders.filter((o) => {
      if (!f.test(o)) return false;
      if (!q) return true;
      return [o.code, o.productName, o.hermandadNombre, o.userName]
        .some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [orders, filtro, busca]);

  // Dentro de "Activos" y "Todos" se agrupa por estado: es más rápido de leer
  // que una lista plana cuando hay pedidos en fases distintas.
  const agrupados = useMemo(() => {
    if (filtro === 'accion') return null;
    const orden: PackOrderStatus[] = [
      'pendiente', 'pago_reportado', 'aceptado', 'en_proceso', 'terminado',
      'en_mod', 'entregado', 'rechazado', 'cancelado',
    ];
    const map = new Map<PackOrderStatus, PackOrder[]>();
    visibles.forEach((o) => {
      if (!map.has(o.status)) map.set(o.status, []);
      map.get(o.status)!.push(o);
    });
    return orden.filter((s) => map.has(s)).map((s) => [s, map.get(s)!] as const);
  }, [visibles, filtro]);

  const urgentes = filtro === 'accion';

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex flex-wrap gap-1.5">
          {FILTROS.map((f) => {
            const n = orders.filter(f.test).length;
            const activo = filtro === f.key;
            const alerta = f.key === 'accion' && n > 0;
            return (
              <button
                key={f.key}
                onClick={() => setFiltro(f.key)}
                className={`flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition-all ${
                  activo
                    ? 'border-primary/40 bg-primary/15 text-primary'
                    : alerta
                      ? 'border-amber-500/35 bg-amber-500/10 text-amber-300 hover:border-amber-500/50'
                      : 'border-white/8 bg-card/40 text-muted-foreground hover:border-white/15 hover:text-foreground'
                }`}
              >
                {alerta && <Zap className="h-3.5 w-3.5" />}
                {f.label}
                <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold ${activo ? 'bg-primary/20' : 'bg-white/8'}`}>
                  {n}
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por código, producto, hermandad o usuario…"
            className="h-10 w-full rounded-xl border border-white/8 bg-card/40 pl-10 pr-4 text-sm outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-primary/40"
          />
        </div>
      </div>

      {cargando ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : visibles.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-white/8 py-20 text-center">
          {urgentes ? (
            <>
              <div className="grid h-14 w-14 place-items-center rounded-2xl border border-emerald-500/25 bg-emerald-500/10 text-emerald-400">
                <Check className="h-7 w-7" />
              </div>
              <p className="mt-3 font-headline text-lg font-bold">Todo al día</p>
              <p className="mt-1 text-sm text-muted-foreground">No hay nada esperando por ti.</p>
            </>
          ) : (
            <>
              <Inbox className="mb-3 h-10 w-10 opacity-30" />
              <p className="text-sm text-muted-foreground">Ningún pedido coincide.</p>
            </>
          )}
        </div>
      ) : agrupados ? (
        <div className="space-y-6">
          {agrupados.map(([status, items]) => (
            <section key={status} className="space-y-2.5">
              <div className="flex items-center gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: ESTADOS[status].color }} />
                <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: ESTADOS[status].color }}>
                  {ESTADOS[status].label}
                </h3>
                <span className="rounded-md bg-white/8 px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">
                  {items.length}
                </span>
                <span className="h-px flex-1 bg-white/8" />
              </div>
              <div className="space-y-2.5">
                {items.map((o) => <PedidoAdmin key={o.id} order={o} />)}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="space-y-2.5">
          {visibles.map((o) => <PedidoAdmin key={o.id} order={o} />)}
        </div>
      )}
    </div>
  );
}

function PedidoAdmin({ order }: { order: PackOrder }) {
  const { toast } = useToast();
  const [abierto, setAbierto] = useState(false);
  const [vista, setVista] = useState<'detalle' | 'chat'>('detalle');
  const [trabajando, setTrabajando] = useState(false);
  const [rechazando, setRechazando] = useState(false);
  const [motivo, setMotivo] = useState('');

  const nuevas = Number(order.unreadAdmin) || 0;
  const e = ESTADOS[order.status] || ESTADOS.pendiente;

  useEffect(() => {
    if (abierto && nuevas > 0) packsShop_markOrderSeen(order.id, 'admin');
  }, [abierto, nuevas, order.id]);

  const revisar = async (decision: 'accept' | 'reject', reason?: string) => {
    setTrabajando(true);
    const r = await packsShop_reviewRequest(order.id, decision, reason);
    setTrabajando(false);
    toast({
      title: r?.success ? 'Hecho' : 'Error',
      description: r?.message,
      variant: r?.success ? undefined : 'destructive',
    });
    if (r?.success) { setRechazando(false); setMotivo(''); }
  };

  const cambiarEstado = async (status: 'paid' | PackOrderStatus, nota?: string) => {
    setTrabajando(true);
    const r = await packsShop_adminSetStatus(order.id, status, nota);
    setTrabajando(false);
    toast({
      title: r?.success ? 'Hecho' : 'Error',
      description: r?.message,
      variant: r?.success ? undefined : 'destructive',
    });
  };

  const avances = AVANCE.filter((a) => a.from.includes(order.status));

  // Los estados que esperan una decisión del admin se marcan visualmente.
  const requiereAccion = order.status === 'pendiente' || order.status === 'pago_reportado';

  return (
    <article
      className="relative overflow-hidden rounded-2xl border bg-card/40 transition-colors"
      style={{
        borderColor: requiereAccion || nuevas > 0 ? hexToRgba(e.color, 0.4) : 'rgba(255,255,255,0.08)',
        background: requiereAccion ? hexToRgba(e.color, 0.05) : undefined,
      }}
    >
      {/* Carril de color del estado: permite barrer la lista sin leer nada */}
      <span className="absolute inset-y-0 left-0 w-1" style={{ background: e.color }} />

      <button
        onClick={() => setAbierto((v) => !v)}
        className="flex w-full items-center gap-3.5 py-3.5 pl-5 pr-4 text-left transition-colors hover:bg-white/[0.03]"
      >
        <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-black/30">
          {order.productImageUrl
            ? <img src={order.productImageUrl} alt="" className="h-full w-full object-cover" />
            : <div className="grid h-full w-full place-items-center"><Gem className="h-5 w-5 text-primary/40" /></div>}
          {nuevas > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-br from-primary to-amber-500 px-1 text-[10px] font-bold text-primary-foreground">
              {nuevas}
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className="rounded-md bg-primary/12 px-1.5 py-0.5 font-mono text-xs font-bold text-primary">
              {order.code}
            </span>
            <span className="truncate font-bold">{order.productName}</span>
            {requiereAccion && (
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                style={{ color: e.color, background: hexToRgba(e.color, 0.16) }}
              >
                <Zap className="h-2.5 w-2.5" /> Te toca
              </span>
            )}
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 truncate">
              <Building2 className="h-3 w-3 opacity-60" /> {order.hermandadNombre}
            </span>
            <span className="inline-flex items-center gap-1 truncate">
              <User className="h-3 w-3 opacity-60" /> {order.userName || 'Sin nombre'}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3 opacity-60" /> {formatFechaHora(order.createdAt)}
            </span>
          </p>
        </div>

        <div className="hidden shrink-0 items-center gap-3 sm:flex">
          <EstadoBadge status={order.status} />
          <span className="w-20 text-right font-headline text-lg font-black text-primary">
            {formatEur(order.priceEur)}
          </span>
        </div>
        <span className="shrink-0 text-muted-foreground">
          {abierto ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {abierto && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-white/8"
          >
            <div className="space-y-5 p-4 md:p-5">
              <div className="flex gap-1.5">
                {([['detalle', 'Detalle'], ['chat', 'Chat']] as const).map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setVista(k)}
                    className={`flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold transition-all ${
                      vista === k
                        ? 'border-primary/40 bg-primary/15 text-primary'
                        : 'border-white/8 bg-card/40 text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {k === 'chat' && <MessageSquare className="h-3.5 w-3.5" />} {label}
                  </button>
                ))}
              </div>

              {vista === 'chat' ? (
                <OrderChat order={order} asAdmin />
              ) : (
                <>
                  <EstadoTimeline order={order} />

                  {/* ── Acciones ── */}
                  {order.status === 'pendiente' && (
                    <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.07] p-4">
                      <p className="text-sm font-bold text-amber-300">Solicitud por revisar</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Al aceptar se reserva una unidad del stock y se le manda al usuario una carta con
                        instrucciones para pagar.
                      </p>
                      {rechazando ? (
                        <div className="mt-3 space-y-2">
                          <Label className="text-xs">Motivo del rechazo (lo verá el usuario)</Label>
                          <Textarea
                            rows={3}
                            value={motivo}
                            onChange={(ev) => setMotivo(ev.target.value)}
                            placeholder="Explica por qué no se puede aceptar…"
                          />
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => { setRechazando(false); setMotivo(''); }}>
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="gap-1.5"
                              disabled={!motivo.trim() || trabajando}
                              onClick={() => revisar('reject', motivo.trim())}
                            >
                              {trabajando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                              Confirmar rechazo
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button size="sm" className="gap-1.5" disabled={trabajando} onClick={() => revisar('accept')}>
                            {trabajando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            Aceptar solicitud
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1.5 text-destructive" onClick={() => setRechazando(true)}>
                            <X className="h-3.5 w-3.5" /> Rechazar
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {order.status === 'pago_reportado' && (
                    <div className="rounded-2xl border border-violet-500/25 bg-violet-500/[0.07] p-4">
                      <p className="flex items-center gap-2 text-sm font-bold text-violet-300">
                        <CreditCard className="h-4 w-4" /> El comprador dice que ya ha pagado
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Método: <strong>{order.paymentMethod === 'bizum' ? 'Bizum' : 'Transferencia'}</strong> ·
                        Importe: <strong>{formatEur(order.priceEur)}</strong> ·
                        Concepto esperado: <strong className="font-mono">{order.code}</strong>
                      </p>
                      <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-300/90">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        Comprueba el ingreso antes de confirmarlo: al hacerlo se abre el chat y arranca la producción.
                      </p>
                    </div>
                  )}

                  {(avances.length > 0 || (order.status !== 'entregado' && order.status !== 'cancelado' && order.status !== 'rechazado' && order.status !== 'pendiente')) && (
                    <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/8 bg-black/20 p-4">
                      <span className="mr-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">Avanzar a</span>
                      {avances.map((a) => (
                        <Button
                          key={a.key}
                          size="sm"
                          variant={a.key === 'paid' ? 'default' : 'outline'}
                          className="gap-1.5"
                          disabled={trabajando}
                          onClick={() => cambiarEstado(a.key)}
                        >
                          {a.key === 'paid' ? <CreditCard className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                          {a.label}
                        </Button>
                      ))}
                      {avances.length === 0 && <span className="text-xs text-muted-foreground">Nada que avanzar desde aquí.</span>}

                      <div className="flex-1" />

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="gap-1.5 text-destructive">
                            <Ban className="h-3.5 w-3.5" /> Cancelar pedido
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Cancelar el pedido {order.code}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Se devolverá la unidad reservada al stock y se avisará al comprador.
                              El dinero, si ya lo pagó, tendrás que devolverlo tú por fuera.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Volver</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive hover:bg-destructive/80"
                              onClick={() => cambiarEstado('cancelado')}
                            >
                              Cancelar pedido
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}

                  {/* ── Encargo ── */}
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold">Qué pide el comprador</h4>
                      <div className="rounded-2xl border border-white/8 bg-black/20 p-4 text-sm text-muted-foreground">
                        <RichText>{order.message}</RichText>
                      </div>
                      {order.images && order.images.length > 0 && (
                        <>
                          <h4 className="text-sm font-bold">Referencias ({order.images.length})</h4>
                          <ImageGrid urls={order.images} />
                        </>
                      )}
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-sm font-bold">Historial</h4>
                      <ol className="space-y-2.5 rounded-2xl border border-white/8 bg-black/20 p-4">
                        {(order.tracking || []).map((t, i) => (
                          <li key={i} className="flex gap-3 text-sm">
                            <span
                              className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                              style={{ background: (ESTADOS as any)[t.status]?.color || '#facc15' }}
                            />
                            <span className="min-w-0">
                              <span className="block font-semibold">{t.note || t.status}</span>
                              <span className="block text-xs text-muted-foreground">{formatFechaHora(t.at)}</span>
                            </span>
                          </li>
                        ))}
                      </ol>
                      {order.status === 'rechazado' && order.rejectionReason && (
                        <div className="rounded-2xl border border-destructive/25 bg-destructive/[0.07] p-4 text-sm">
                          <p className="font-bold text-destructive">Motivo del rechazo</p>
                          <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{order.rejectionReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </article>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PRODUCTOS
// ═══════════════════════════════════════════════════════════════════════════

function PanelProductos({ products }: { products: PackProduct[] }) {
  const { toast } = useToast();
  const [editando, setEditando] = useState<PackProduct | 'new' | null>(null);

  const borrar = async (id: string) => {
    const res = await deletePackProduct(id);
    toast({
      title: res.success ? 'Eliminado' : 'Error',
      description: res.message,
      variant: res.success ? undefined : 'destructive',
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{products.length} producto(s) en el catálogo.</p>
        <Button className="gap-2" onClick={() => setEditando('new')}>
          <Plus className="h-4 w-4" /> Nuevo producto
        </Button>
      </div>

      {products.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-white/8 py-16 text-muted-foreground">
          <Package className="mb-3 h-10 w-10 opacity-30" />
          <p className="text-sm">No hay productos todavía.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {products.map((p) => {
            const st = stockInfo(p.stock);
            const sinPrecio = !(Number(p.priceEur) > 0);
            return (
              <div key={p.id} className="overflow-hidden rounded-2xl border border-white/8 bg-card/40">
                <div className="relative aspect-video">
                  {p.imageUrl
                    ? <img src={p.imageUrl} alt="" className="h-full w-full object-cover" />
                    : <div className="grid h-full w-full place-items-center bg-muted/20"><Gem className="h-8 w-8 text-muted-foreground/30" /></div>}
                  <span
                    className="absolute right-2 top-2 rounded-lg border px-2 py-0.5 text-[11px] font-bold backdrop-blur-sm"
                    style={{ color: st.color, borderColor: hexToRgba(st.color, 0.35), background: hexToRgba(st.color, 0.15) }}
                  >
                    {st.label}
                  </span>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold leading-tight">
                      {p.name}{' '}
                      {p.active === false && <Badge className="bg-zinc-500/20 text-zinc-300">Inactivo</Badge>}
                      {p.featured && <Badge className="bg-primary/20 text-primary">Destacado</Badge>}
                    </h3>
                    <span className={`shrink-0 font-headline font-black ${sinPrecio ? 'text-destructive' : 'text-primary'}`}>
                      {sinPrecio ? 'Sin precio' : formatEur(p.priceEur)}
                    </span>
                  </div>
                  {p.category && <p className="mt-0.5 text-xs text-muted-foreground">{p.category}</p>}
                  {sinPrecio && (
                    <p className="mt-2 flex items-center gap-1.5 rounded-lg border border-destructive/25 bg-destructive/10 px-2 py-1 text-[11px] text-destructive">
                      <AlertTriangle className="h-3 w-3" /> Ponle un importe en € o no se podrá cobrar.
                    </p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => setEditando(p)}>
                      <Pencil className="h-3.5 w-3.5" /> Editar
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="gap-1 text-destructive">
                          <Trash2 className="h-3.5 w-3.5" /> Eliminar
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar "{p.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción es permanente. Los pedidos ya hechos conservan su copia de los datos.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => borrar(p.id)} className="bg-destructive hover:bg-destructive/80">
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editando && (
        <ProductEditor product={editando === 'new' ? null : editando} onClose={() => setEditando(null)} />
      )}
    </div>
  );
}

function ProductEditor({ product, onClose }: { product: PackProduct | null; onClose: () => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: product?.name || '',
    description: product?.description || '',
    priceEur: String(product?.priceEur ?? ''),
    category: product?.category || '',
    includes: (product?.includes || []).join('\n'),
    imageUrl: product?.imageUrl || '',
    stock: String(product?.stock ?? -1),
    ilimitado: (product?.stock ?? -1) < 0,
    active: product?.active !== false,
    featured: product?.featured === true,
    order: String(product?.order ?? 0),
  });
  const [subiendo, setSubiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const subir = async (file: File) => {
    setSubiendo(true);
    const res = await uploadPackImage(file);
    setSubiendo(false);
    if (res.success && res.url) set('imageUrl', res.url);
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
  };

  const guardar = async () => {
    if (!form.name.trim()) { toast({ title: 'Falta el nombre', variant: 'destructive' }); return; }
    const precio = Number(String(form.priceEur).replace(',', '.'));
    if (!Number.isFinite(precio) || precio <= 0) {
      toast({ title: 'Precio no válido', description: 'Pon el importe en euros (ej. 12,50).', variant: 'destructive' });
      return;
    }
    setGuardando(true);
    const payload: Partial<PackProduct> = {
      name: form.name.trim(),
      description: form.description.trim(),
      priceEur: precio,
      // Se mantiene el texto legacy en sync para que nada quede descolgado.
      price: precio.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }),
      category: form.category.trim(),
      includes: form.includes.split('\n').map((s) => s.trim()).filter(Boolean),
      imageUrl: form.imageUrl,
      stock: form.ilimitado ? -1 : Math.max(0, Math.floor(Number(form.stock) || 0)),
      active: form.active,
      featured: form.featured,
      order: Number(form.order) || 0,
    };
    const res = product ? await updatePackProduct(product.id, payload) : await createPackProduct(payload);
    setGuardando(false);
    toast({
      title: res.success ? 'Guardado' : 'Error',
      description: res.message,
      variant: res.success ? undefined : 'destructive',
    });
    if (res.success) onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90dvh] max-w-lg flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>{product ? 'Editar producto' : 'Nuevo producto'}</DialogTitle>
          <DialogDescription>Foto, importe en euros, stock y descripción.</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6 pt-4">
          <div className="space-y-1.5">
            <Label>Imagen</Label>
            {form.imageUrl ? (
              <div className="relative overflow-hidden rounded-lg border">
                <img src={form.imageUrl} alt="" className="block aspect-video w-full object-cover" />
                <Button size="sm" variant="secondary" className="absolute right-2 top-2" onClick={() => set('imageUrl', '')}>
                  Quitar
                </Button>
              </div>
            ) : (
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 px-3 py-3 text-sm text-muted-foreground hover:bg-white/5">
                {subiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                Subir foto (o pegar URL abajo)
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(ev) => ev.target.files?.[0] && subir(ev.target.files[0])}
                />
              </label>
            )}
            <Input value={form.imageUrl} onChange={(ev) => set('imageUrl', ev.target.value)} placeholder="https://... (opcional)" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={form.name} onChange={(ev) => set('name', ev.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Precio (€)</Label>
              <Input
                inputMode="decimal"
                value={form.priceEur}
                onChange={(ev) => set('priceEur', ev.target.value)}
                placeholder="12,50"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Categoría</Label>
              <Input value={form.category} onChange={(ev) => set('category', ev.target.value)} placeholder="Ej: Enseres, Pasos…" />
            </div>
            <div className="space-y-1.5">
              <Label>Orden</Label>
              <Input type="number" value={form.order} onChange={(ev) => set('order', ev.target.value)} />
            </div>
          </div>

          {/* Stock: la unidad se reserva cuando el admin ACEPTA la solicitud. */}
          <div className="space-y-2 rounded-xl border border-white/8 p-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2"><Boxes className="h-4 w-4" /> Stock ilimitado</Label>
              <Switch checked={form.ilimitado} onCheckedChange={(v) => set('ilimitado', v)} />
            </div>
            {!form.ilimitado && (
              <div className="space-y-1.5">
                <Label className="text-xs">Unidades disponibles</Label>
                <Input type="number" min={0} value={form.stock} onChange={(ev) => set('stock', ev.target.value)} />
                <p className="text-[11px] text-muted-foreground">
                  Se descuenta una unidad al <strong>aceptar</strong> una solicitud, y se devuelve si se cancela.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Descripción</Label>
            <RichTextarea rows={3} value={form.description} onChange={(ev) => set('description', ev.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Incluye (una línea por elemento, opcional)</Label>
            <Textarea
              rows={3}
              value={form.includes}
              onChange={(ev) => set('includes', ev.target.value)}
              placeholder="Cruz de guía&#10;Faroles&#10;Ciriales"
            />
          </div>

          <div className="flex items-center justify-between rounded-xl border border-white/8 p-3">
            <Label>Activo (visible en la tienda)</Label>
            <Switch checked={form.active} onCheckedChange={(v) => set('active', v)} />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-white/8 p-3">
            <Label>Destacado (arriba del todo, a doble ancho)</Label>
            <Switch checked={form.featured} onCheckedChange={(v) => set('featured', v)} />
          </div>
        </div>

        <DialogFooter className="border-t border-white/8 px-6 py-4">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando || subiendo}>
            {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// AJUSTES
// ═══════════════════════════════════════════════════════════════════════════

function PanelAjustes() {
  const { toast } = useToast();
  const [cfg, setCfg] = useState<PacksShopConfig | null>(null);
  const [pago, setPago] = useState<PacksPaymentInfo | null>(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    getPacksShopConfig().then(setCfg);
    getPacksPaymentConfig().then(setPago);
  }, []);

  const guardar = async () => {
    setGuardando(true);
    const [a, b] = await Promise.all([
      savePacksShopConfig(cfg || {}),
      savePacksPaymentConfig(pago || {}),
    ]);
    setGuardando(false);
    const ok = a.success && b.success;
    toast({
      title: ok ? 'Ajustes guardados' : 'Error',
      description: ok ? 'Los cambios ya están activos.' : (a.message || b.message),
      variant: ok ? undefined : 'destructive',
    });
  };

  if (!cfg || !pago) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const setC = (k: keyof PacksShopConfig, v: any) => setCfg((p) => ({ ...(p || {}), [k]: v }));
  const setP = (k: keyof PacksPaymentInfo, v: any) => setPago((p) => ({ ...(p || {}), [k]: v }));

  return (
    <div className="space-y-5">
      {/* Datos de cobro */}
      <section className="space-y-4 rounded-2xl border border-white/8 bg-card/40 p-5">
        <div>
          <h3 className="flex items-center gap-2 font-headline text-lg font-bold">
            <CreditCard className="h-5 w-5 text-primary" /> Datos de cobro
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Solo se los enseñamos al comprador cuando su solicitud está aceptada. Viven en
            <span className="font-mono"> packs_config/main</span>, que no es de lectura pública.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-3 rounded-xl border border-white/8 bg-black/20 p-4">
            <p className="text-sm font-bold text-sky-300">Bizum</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Número de teléfono</Label>
              <Input value={pago.bizumNumber || ''} onChange={(e) => setP('bizumNumber', e.target.value)} placeholder="600 000 000" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">A nombre de</Label>
              <Input value={pago.bizumHolder || ''} onChange={(e) => setP('bizumHolder', e.target.value)} placeholder="Nombre y apellidos" />
            </div>
          </div>

          <div className="space-y-3 rounded-xl border border-white/8 bg-black/20 p-4">
            <p className="text-sm font-bold text-amber-300">Transferencia</p>
            <div className="space-y-1.5">
              <Label className="text-xs">IBAN</Label>
              <Input value={pago.iban || ''} onChange={(e) => setP('iban', e.target.value)} placeholder="ES00 0000 0000 0000 0000 0000" className="font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Titular</Label>
                <Input value={pago.ibanHolder || ''} onChange={(e) => setP('ibanHolder', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Banco</Label>
                <Input value={pago.bankName || ''} onChange={(e) => setP('bankName', e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Instrucciones extra (opcional, se ven junto a los datos de pago)</Label>
          <RichTextarea rows={3} value={pago.notes || ''} onChange={(e) => setP('notes', e.target.value)} />
        </div>
      </section>


      {/* Tienda */}
      <section className="space-y-4 rounded-2xl border border-white/8 bg-card/40 p-5">
        <h3 className="flex items-center gap-2 font-headline text-lg font-bold">
          <Settings className="h-5 w-5 text-primary" /> Tienda
        </h3>

        <div className="flex items-center justify-between rounded-xl border border-white/8 p-3">
          <div>
            <Label>Tienda abierta</Label>
            <p className="text-xs text-muted-foreground">Si la cierras, nadie puede enviar solicitudes nuevas.</p>
          </div>
          <Switch checked={cfg.open !== false} onCheckedChange={(v) => setC('open', v)} />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Aviso destacado en la cabecera (opcional)</Label>
          <RichTextarea rows={2} value={cfg.announcement || ''} onChange={(e) => setC('announcement', e.target.value)} />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Términos y condiciones (hay que aceptarlos al solicitar)</Label>
          <RichTextarea rows={8} value={cfg.terms || ''} onChange={(e) => setC('terms', e.target.value)} placeholder="Déjalo vacío para usar los términos por defecto." />
        </div>

        {cfg.terms && (
          <div className="rounded-xl border border-white/8 bg-black/20 p-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Vista previa</p>
            <div className="text-sm text-muted-foreground"><RichText>{cfg.terms}</RichText></div>
          </div>
        )}
      </section>

      <Button size="lg" className="w-full gap-2" onClick={guardar} disabled={guardando}>
        {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Guardar ajustes
      </Button>
    </div>
  );
}
